"use client";
import React, { useState } from "react";
import { useParams } from "react-router-dom";
import ReviewList from "./ReviewList";
import { RatingProvider, useRating } from "../../context/RatingContext"; // adjust path as needed

const CircularRating = ({ rating, size = 93, className = "" }) => {
  const percentage = (rating / 5) * 100;
  const radius = (size - 8) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <div className={`relative ${className}`} style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#e5e7eb"
          strokeWidth="4"
          fill="none"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#fbbf24"
          strokeWidth="4"
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          className="transition-all duration-300"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-xl sm:text-2xl font-bold text-neutral-900">{rating.toFixed(1)}</span>
      </div>
    </div>
  );
};

function ProductReviewsContent() {
  const { ratingData, averageRating, totalReviews, loading, error } = useRating();
  const [activeFilter, setActiveFilter] = useState("all");

  const handleFilterClick = (filter) => {
    setActiveFilter(filter);
  };

  const handleKeyDown = (e, filter) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleFilterClick(filter);
    }
  };

  if (loading) return <p className="text-center py-6">Loading ratings...</p>;
  if (error) return <p className="text-center text-red-600 py-6">Error: {error}</p>;

  return (
    <section className="flex flex-col w-full">
      <h2 className="text-3xl font-semibold leading-tight text-neutral-900 mb-7">
        Product Reviews
      </h2>

      {/* Rating Overview */}
      <div className="p-4 sm:p-6 w-full rounded-lg border border-dashed border-zinc-400">
        <div className="flex gap-5 max-md:flex-col">
          {/* Mobile */}
          <div className="md:hidden w-full">
            <div className="flex gap-5 items-center justify-center">
              <CircularRating rating={averageRating} size={80} />
              <div className="flex flex-col">
                <div className="flex gap-1 mb-2">
                  {[...Array(5)].map((_, index) => (
                    <svg key={index} viewBox="0 0 24 24" fill="currentColor" className="h-[18px] w-[18px] text-yellow-400">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                    </svg>
                  ))}
                </div>
                <p className="text-base text-neutral-600">
                  from {(totalReviews >= 1000 ? (totalReviews / 1000).toFixed(1) + "k" : totalReviews)} reviews
                </p>
              </div>
            </div>
          </div>

          {/* Desktop */}
          <div className="hidden md:flex gap-5 w-full">
            <div className="w-[30%] flex flex-col items-center">
              <CircularRating rating={averageRating} size={93} className="mb-4" />
              <div className="flex flex-col items-center">
                <div className="flex gap-1 mb-2">
                  {[...Array(5)].map((_, index) => (
                    <svg key={index} viewBox="0 0 24 24" fill="currentColor" className="h-[22px] w-[22px] text-yellow-400">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                    </svg>
                  ))}
                </div>
                <p className="text-lg text-neutral-600 text-center">
                  from {(totalReviews >= 1000 ? (totalReviews / 1000).toFixed(1) + "k" : totalReviews)} reviews
                </p>
              </div>
            </div>

            {/* Rating Breakdown */}
            <div className="w-[70%] pl-5">
              {ratingData.map((rating, index) => (
                <div key={index} className="flex items-center gap-4 mt-3.5 first:mt-0">
                  <div className="flex gap-1 items-center text-lg font-medium min-w-[60px]">
                    {rating.stars}.0
                    <svg viewBox="0 0 24 24" fill="currentColor" className="h-[22px] w-[22px] text-yellow-400">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                    </svg>
                  </div>
                  <div className="flex-1 flex items-center gap-4">
                    <div className="flex-1 bg-slate-200 rounded-lg h-2">
                      <div
                        className="bg-zinc-800 h-2 rounded-lg"
                        style={{ width: `${rating.percentage}%` }}
                        role="progressbar"
                        aria-valuenow={rating.percentage}
                        aria-valuemin="0"
                        aria-valuemax="100"
                        aria-label={`${rating.stars} star rating: ${rating.percentage}%`}
                      />
                    </div>
                    <div className="text-lg font-medium min-w-[43px] text-right">{rating.count}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      
      {/* Review Filters */}
      {totalReviews > 0 ? (
        <div className="mt-12 w-full">

          <ReviewList activeFilter={activeFilter} />
        </div>
      ) : (
        <p className="text-center py-8 text-gray-500">No reviews yet for this product.</p>
      )}

          </section>
        );
      }

// Main wrapper that uses RatingProvider
function ProductReviews() {
  const { id: productId } = useParams();

  return (
    <RatingProvider productId={productId}>
      <ProductReviewsContent />
    </RatingProvider>
  );
}

export default ProductReviews;
